import { useState } from 'react';
import {
  Box, Button, CircularProgress, Menu, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ACCENT } from './offerteUi';
import OfferteRegels from './OfferteRegels';
import { korteBasisNaam } from './basisofferte';

/**
 * Het offerte-document: titel, omschrijving, regeltabel, totaal. Meer niet.
 * Presentatie-only — alle state en handlers komen uit de container.
 *
 * De omschrijving blijft bewust een markdown-textarea (geen WYSIWYG): de
 * heen-en-weer-conversie markdown ↔ Gripp-HTML is precies waar tekst stukgaat.
 */
function OfferteDocument({
  draft, onSubjectChange, onDescriptionChange,
  setLines, sortLinesBySection, catalogus, onLoadCatalogus, standaardBtw,
  genLines, linesLoading, priceCheck, products,
  basisOffers, onLoadBasisOffers, onStartFromBasis,
}) {
  const [basisAnchor, setBasisAnchor] = useState(null);
  // De omschrijving is al gauw een paar schermen hoog; dichtklappen geeft lucht
  // zodra je met de regels of de chat bezig bent (verzoek 20 aug).
  const [descOpen, setDescOpen] = useState(true);
  if (!draft) return null;
  const lines = draft.lines || [];
  const descRegels = (draft.description || '').split('\n').filter((r) => r.trim());

  return (
    <Box>
      {/* Titel — als documentkop, niet als formulierveldje */}
      <TextField fullWidth variant="standard" placeholder="Titel van de offerte"
        value={draft.subject || ''} onChange={(e) => onSubjectChange(e.target.value)}
        InputProps={{ disableUnderline: false, sx: { fontSize: 24, fontWeight: 800, color: '#111827' } }}
        sx={{ mb: 2, '& .MuiInput-underline:before': { borderBottomColor: 'transparent' } }} />

      {/* Omschrijving — inklapbaar, want al gauw een paar schermen hoog */}
      <Stack direction="row" alignItems="center" spacing={0.5} onClick={() => setDescOpen((v) => !v)}
        sx={{ cursor: 'pointer', userSelect: 'none', width: 'fit-content' }}>
        <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em' }}>
          OMSCHRIJVING (markdown; pas gerust aan)
        </Typography>
        {descOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: '#94a3b8' }} />}
        {!descOpen && (
          <Typography variant="caption" sx={{ color: '#cbd5e1' }}>· {descRegels.length} regels — klik om te openen</Typography>
        )}
      </Stack>
      {descOpen ? (
        <TextField fullWidth multiline minRows={8} maxRows={28} size="small" value={draft.description || ''}
          onChange={(e) => onDescriptionChange(e.target.value)}
          sx={{ mt: 0.5, mb: 3, '& textarea': { fontFamily: 'monospace', fontSize: 14.5, lineHeight: 1.65 } }} />
      ) : (
        <Box onClick={() => setDescOpen(true)} sx={{
          mt: 0.5, mb: 3, p: 1.25, borderRadius: 1.5, border: '1px dashed #e2e8f0', bgcolor: '#fafbfc',
          cursor: 'pointer', '&:hover': { bgcolor: '#f1f5f9' },
        }}>
          <Typography variant="body2" sx={{
            fontFamily: 'monospace', fontSize: 13, color: '#64748b', whiteSpace: 'pre-wrap',
            display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {draft.description || ''}
          </Typography>
        </Box>
      )}

      {/* Offerteregels */}
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{
        mb: 1,
        ...(lines.length === 0 && {
          p: 1.5, borderRadius: 2, border: '1px solid #c7d2fe', bgcolor: '#eef2ff',
        }),
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, letterSpacing: '0.06em' }}>
            OFFERTEREGELS
          </Typography>
          {lines.length === 0 && (
            <Typography variant="body2" sx={{ color: '#4338ca' }}>
              Zonder regels gaat er een lege offerte naar Gripp — genereer ze uit de omschrijving (daarna kun je alles aanpassen).
            </Typography>
          )}
        </Box>
        <Button size="small" variant={lines.length ? 'text' : 'contained'}
          startIcon={linesLoading ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon />}
          onClick={genLines} disabled={linesLoading || !draft.description}
          sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap',
            ...(!lines.length && { bgcolor: ACCENT.main, '&:hover': { bgcolor: ACCENT.dark } }) }}>
          {linesLoading ? 'Regels genereren…' : (lines.length ? 'Opnieuw genereren' : 'Genereer offerteregels')}
        </Button>
        {/* Startpunt = een Gripp-basisofferte (bron van waarheid voor opbouw en prijzen). */}
        {onStartFromBasis && lines.length === 0 && (
          <>
            <Button size="small" variant="text" disabled={linesLoading}
              onClick={(e) => { setBasisAnchor(e.currentTarget); if (onLoadBasisOffers) onLoadBasisOffers(); }}
              sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', color: ACCENT.dark }}>
              Start uit basisofferte
            </Button>
            <Menu anchorEl={basisAnchor} open={!!basisAnchor} onClose={() => setBasisAnchor(null)}>
              {basisOffers == null && <MenuItem disabled>Basisoffertes laden…</MenuItem>}
              {basisOffers && basisOffers.length === 0 && (
                <MenuItem disabled>Nog geen basisoffertes gespiegeld (Beheer → Basisoffertes)</MenuItem>
              )}
              {(basisOffers || []).filter((b) => !b.engels).map((b) => (
                <MenuItem key={b.id} onClick={() => { setBasisAnchor(null); onStartFromBasis(b.id); }}
                  sx={{ fontSize: 14, fontWeight: b.leidendVoor?.length ? 700 : 400 }}>
                  {korteBasisNaam(b.naam)}
                  <Typography component="span" variant="caption" sx={{ color: '#94a3b8', ml: 1 }}>
                    {b.aantalRegels} regels
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>

      {lines.length > 0 && (
        <>
          <OfferteRegels lines={lines} setLines={setLines} priceCheck={priceCheck} products={products}
            catalogus={catalogus} onLoadCatalogus={onLoadCatalogus} standaardBtw={standaardBtw} />
          <Button size="small" onClick={sortLinesBySection} sx={{ textTransform: 'none', color: '#64748b', mt: 0.5 }}>
            ↕ Sorteer secties in de standaardvolgorde
          </Button>
        </>
      )}
    </Box>
  );
}

export default OfferteDocument;
